import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import api from '../services/api';
import { AnalyticsShell, StatCard } from '../components/analytics/AnalyticsShell';
import { Stagger, fadeUp } from '../components/analytics/anim';
import { motion } from 'framer-motion';
import { dur } from '../components/analytics/format';

export const Route = createFileRoute('/analytics/actors/$actorId')({
  component: ActorDetail,
});

interface ActorView {
  viewId: string;
  shareToken: string;
  title: string | null;
  providerKey: string | null;
  variant: string;
  registration: string | null;
  startedAt: string;
  endedAt: string | null;
  watchTimeMs: number;
  playingTimeMs: number;
  maxPercent: number;
  completed: boolean;
  deviceType: string | null;
  geoCountry: string | null;
  embedOrigin: string | null;
  referrer: string | null;
  hasCoverage: boolean;
  events: number;
  interactions: number;
}

interface TimelineRow {
  kind: 'event' | 'interaction';
  viewId: string;
  shareToken: string;
  at: string;
  event?: string;
  verb?: string;
  interactionId?: string;
  interactionType?: string;
  response?: string | null;
  isCorrect?: boolean | null;
  latencyMs?: number | null;
  slideId?: string | null;
  slideIndex?: number | null;
  playheadMs?: number | null;
  percent?: number | null;
  properties?: Record<string, unknown>;
}

interface ActorPayload {
  actor: string;
  kpis: {
    views: number;
    videos: number;
    completes: number;
    avgPercentViewed: number;
    watchMs: number;
    firstSeenAt: string;
    lastSeenAt: string;
  };
  perVideo: Array<{
    shareToken: string;
    title: string | null;
    providerKey: string | null;
    views: number;
    completes: number;
    bestPercentViewed: number;
    watchMs: number;
    firstViewedAt: string;
    lastViewedAt: string;
  }>;
  views: ActorView[];
  timeline: TimelineRow[];
}

function ActorDetail() {
  const { actorId } = Route.useParams();
  const [data, setData] = useState<ActorPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewFilter, setViewFilter] = useState<string>('');

  useEffect(() => {
    setData(null);
    api
      .get(`/analytics/dashboard/actors/${encodeURIComponent(actorId)}`)
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(
          e?.response?.status === 403
            ? 'Admin access required — this dashboard is restricted to administrators.'
            : e?.response?.status === 404
              ? 'No records for this learner.'
              : e?.response?.data?.error ?? e?.message ?? 'Failed to load',
        ),
      );
  }, [actorId]);

  // Each view gets a stable ordinal so the timeline can say WHICH watch a row
  // belongs to — the whole point of keeping repeat views distinct.
  const ordinal = new Map<string, number>();
  if (data) {
    [...data.views]
      .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
      .forEach((v, i) => ordinal.set(v.viewId, i + 1));
  }

  const timeline = (data?.timeline ?? []).filter((t) => !viewFilter || t.viewId === viewFilter);

  return (
    <AnalyticsShell
      crumb={
        <span className="font-mono text-xs">
          <Link to="/analytics/actors" className="text-muted-foreground hover:text-foreground">
            Learners
          </Link>
          <span className="mx-1.5 text-border">/</span>
          {actorId}
        </span>
      }
    >
      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {!data && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {data && (
        <>
          <div className="mb-6">
            <h1 className="break-all font-mono text-xl font-semibold tracking-tight">{data.actor}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Opaque learner id from the launch URL. Unsigned — anyone who can construct the URL can present this id,
              so treat it as an attribution key, not an authenticated identity.
            </p>
          </div>

          <Stagger className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <StatCard label="Views" value={data.kpis.views} accent />
            <StatCard label="Videos" value={data.kpis.videos} />
            <StatCard label="Watch time" value={dur(data.kpis.watchMs)} />
            <StatCard label="Avg % viewed" value={`${data.kpis.avgPercentViewed ?? 0}%`} />
            <StatCard label="Completes" value={data.kpis.completes} />
          </Stagger>

          <Panel title="Videos" subtitle={`${data.perVideo.length} watched`}>
            <div className="-mx-5 -mb-5 overflow-x-auto">
              <table className="w-full text-sm">
                <Head cols={['Video', 'Provider', 'Views', 'Watch', 'Best %', 'Last watched', '']} />
                <tbody>
                  {data.perVideo.map((v) => (
                    <tr key={v.shareToken} className="border-b border-border/40 last:border-0 hover:bg-muted/40">
                      <td className="px-5 py-2.5">{v.title || '(untitled)'}</td>
                      <td className="px-5 py-2.5 text-xs text-muted-foreground">{v.providerKey ?? '—'}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{v.views}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{dur(v.watchMs)}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{v.bestPercentViewed}%</td>
                      <td className="whitespace-nowrap px-5 py-2.5 text-right text-xs text-muted-foreground">
                        {new Date(v.lastViewedAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-2.5 text-right text-xs text-primary">
                        <Link to="/analytics/$shareToken" params={{ shareToken: v.shareToken }}>
                          Video →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="Views"
            subtitle={`${data.views.length} distinct watches · every repeat watch is its own record`}
          >
            <div className="-mx-5 -mb-5 overflow-x-auto">
              <table className="w-full text-sm">
                <Head
                  cols={['#', 'Started', 'Video', 'Watch', '% viewed', 'Done', 'Device', 'Origin', 'Events', 'Ix', '']}
                />
                <tbody>
                  {data.views.map((v) => (
                    <motion.tr
                      key={v.viewId}
                      variants={fadeUp}
                      onClick={() => setViewFilter(viewFilter === v.viewId ? '' : v.viewId)}
                      className={`cursor-pointer border-b border-border/40 last:border-0 hover:bg-muted/40 ${
                        viewFilter === v.viewId ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="px-5 py-2.5 tabular-nums text-muted-foreground">#{ordinal.get(v.viewId)}</td>
                      <td className="whitespace-nowrap px-5 py-2.5">{new Date(v.startedAt).toLocaleString()}</td>
                      <td className="max-w-[16rem] truncate px-5 py-2.5">{v.title || v.shareToken}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{dur(v.watchTimeMs)}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{v.maxPercent}%</td>
                      <td className="px-5 py-2.5 text-center">{v.completed ? <span className="text-primary">✓</span> : ''}</td>
                      <td className="px-5 py-2.5">{v.deviceType ?? '—'}</td>
                      <td className="max-w-[14rem] truncate px-5 py-2.5 text-xs text-muted-foreground">
                        {v.embedOrigin ?? v.referrer ?? '—'}
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{v.events}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{v.interactions}</td>
                      <td className="px-5 py-2.5 text-right text-xs text-primary">
                        {viewFilter === v.viewId ? 'Filtering ✓' : 'Filter timeline'}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="Timeline"
            subtitle={
              viewFilter
                ? `view #${ordinal.get(viewFilter)} only · ${timeline.length} records — click the view again to clear`
                : `${timeline.length} records across every view, newest first`
            }
          >
            {timeline.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Nothing recorded.</div>
            ) : (
              <ol className="-mb-2 space-y-1.5">
                {timeline.map((t, i) => (
                  <li
                    key={`${t.kind}-${i}`}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg px-3 py-2 text-sm odd:bg-muted/30"
                  >
                    <span className="w-40 shrink-0 font-mono text-[11px] text-muted-foreground">
                      {new Date(t.at).toLocaleString()}
                    </span>
                    <span className="w-8 shrink-0 text-[11px] text-muted-foreground">#{ordinal.get(t.viewId)}</span>
                    {t.kind === 'event' ? (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{t.event}</span>
                    ) : (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] text-primary">
                        {t.verb}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t.kind === 'interaction'
                        ? [
                            t.interactionId,
                            t.response != null ? `→ ${t.response}` : null,
                            t.isCorrect === true ? '✓ correct' : t.isCorrect === false ? '✗ wrong' : null,
                            t.latencyMs != null ? `${Math.round(t.latencyMs / 100) / 10}s` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : [
                            t.slideId,
                            t.percent != null ? `${t.percent}%` : null,
                            t.playheadMs != null ? `@${Math.round(t.playheadMs / 1000)}s` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </>
      )}
    </AnalyticsShell>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-xl border border-border/60 bg-card p-5">
      <div className="mb-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Head({ cols }: { cols: string[] }) {
  return (
    <thead className="border-b border-border/60 text-[11px] uppercase tracking-wide text-muted-foreground">
      <tr>
        {cols.map((c, i) => (
          <th key={i} className={`px-5 py-2.5 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}
